import { v4 } from 'uuid'
/* eslint-disable no-prototype-builtins */
import { Form } from 'react-final-form'
import { generateFormFields } from './utils'

import {
  getMemoryListService,
  getVisitService
} from '../../../../../../../services/Services'
import {
  useAppDispatch,
  useAppSelector
} from '../../../../../../../store/hooks'
import {
  addVisit,
  updateVisit
} from '../../../../../../../reducers/currentVisitsReducer'
import { useAuthState } from '../../../../../../../auth/AuthProvider'
import {
  pushStagedUpdates,
  resetStagedUpdates,
  selectStagedUpdates
} from '../../../../../../../reducers/memoryListsReducer'
import {
  Accordion,
  Button,
  Flex,
  SimpleGrid,
  useToast,
  VStack
} from '@chakra-ui/react'
import { type FormApi } from 'final-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useAccount } from '../../../../../account/AccountProvider'
import { useRef } from 'react'
import { useIntl } from 'react-intl'
import messages from '../../messages'

interface Props {
  isEditMode?: boolean
  currentVisitValues?: any
  isFollowUp: boolean
  sortOrder: 'ASC' | 'DESC'
  onClose: () => void
}

export default function VisitForm({
  isEditMode,
  isFollowUp,
  currentVisitValues,
  sortOrder,
  onClose
}: Props) {
  const containerRef = useRef()
  const dispatch = useAppDispatch()
  const { uid } = useAuthState()
  const toast = useToast()
  const { formatMessage } = useIntl()
  const { schema } = useAccount()
  const operationId = useRef(v4())
  const { patientId } = useParams()
  const navigate = useNavigate()

  const memoryListUpdates = useAppSelector(selectStagedUpdates)

  if (!patientId) {
    navigate('/')
    return
  }

  const handleVisitSubmit = async (newValues: any, form: FormApi<any, any>) => {
    try {
      if (isEditMode) {
        const updatedValues: any = { isFollowUp }

        // todo extract this logic
        for (const key in currentVisitValues) {
          if (newValues.hasOwnProperty(key)) {
            updatedValues[key] = newValues[key] // its in old and new, means it was updated
          } else {
            updatedValues[key] = null // its in old, but not in new, means it was cleared
          }
        }

        for (const key in newValues) {
          if (!currentVisitValues?.hasOwnProperty(key)) {
            updatedValues[key] = newValues[key] // its in new but not old, means it is completely new
          }
        }

        const updatedVisit = await getVisitService().updateVisit(
          patientId,
          currentVisitValues.id,
          {
            ...updatedValues,
            revision: currentVisitValues.revision ?? 0,
            operationId: operationId.current
          }
        )
        dispatch(updateVisit(updatedVisit))
        toast({
          title: formatMessage(messages.visitUpdated),
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      } else {
        const newVisit = {
          ...newValues,
          isFollowUp,
          operationId: operationId.current
        }
        const newVisitResponse = await getVisitService().createVisit(
          patientId,
          newVisit
        )
        dispatch(addVisit({ visit: newVisitResponse, sortOrder }))
        toast({
          title: formatMessage(messages.visitCreated),
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      }
      try {
        await getMemoryListService().updateUserMemoryList({
          ownerId: uid,
          updates: memoryListUpdates
        })
        dispatch(pushStagedUpdates())
        dispatch(resetStagedUpdates())
      } catch {
        toast({
          title: formatMessage({
            id: 'reports.suggestionSyncFailed',
            defaultMessage: 'Visit saved. Form suggestions could not be synced.'
          }),
          status: 'warning'
        })
      }
      form.reset()
      onClose()
      operationId.current = v4()
    } catch (error) {
      toast({ title: (error as Error).message, status: 'error' })
    }
  }

  return (
    <Form
      onSubmit={handleVisitSubmit}
      initialValues={isEditMode ? currentVisitValues : {}}
      render={({ handleSubmit, hasValidationErrors, submitting }) => (
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch" px={6} py={6}>
            <Accordion allowMultiple>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {schema.visit.fields.map((field) =>
                  generateFormFields({
                    field,
                    subCategoryLevel: 0,
                    isFollowUp,
                    containerRef
                  })
                )}
              </SimpleGrid>
            </Accordion>
          </VStack>
          <Flex
            position="sticky"
            bottom={0}
            justifyContent="flex-end"
            py={4}
            px={6}
            borderTopWidth="1px"
            borderColor="gray.100"
            gap={3}
            bg="white"
            zIndex={10}
          >
            <Button variant="ghost" size="lg" onClick={onClose}>
              {formatMessage(messages.close)}
            </Button>
            <Button
              colorScheme="blue"
              size="lg"
              px={10}
              type="submit"
              isDisabled={hasValidationErrors || submitting}
              isLoading={submitting}
            >
              {formatMessage(messages.save)}
            </Button>
          </Flex>
        </form>
      )}
    />
  )
}
